import {
  BookOpen,
  ClipboardList,
  CalendarClock,
  CreditCard,
  FileSpreadsheet,
  HeartPulse,
  History,
  KeyRound,
  LayoutDashboard,
  Monitor,
  PackageSearch,
  Receipt,
  Settings as SettingsIcon,
  ShieldCheck,
  Stethoscope,
  Tag,
  Users,
  Wallet,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import type { AuthUser } from '@/stores/authStore';

import logo_novapet_image from '@/assets/pets/logo novapet.png';
import { cn } from '@/shared/lib/cn';

interface NavItem {
  to: string;
  label_key: string;
  icon: typeof LayoutDashboard;
  /** ANY of these permissions grants visibility. Empty = visible to anyone. */
  any_permission?: readonly string[];
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/app/dashboard', label_key: 'nav.dashboard', icon: LayoutDashboard, any_permission: ['admin.dashboard.view'] },
  { to: '/app/customer-statement', label_key: 'nav.customer_statement', icon: FileSpreadsheet, any_permission: ['admin.reports.view', 'open_accounts.payment'] },
  { to: '/app/cashier', label_key: 'nav.cashier_pos', icon: CreditCard, any_permission: ['pos.cashier.use'] },
  { to: '/app/sales-history', label_key: 'nav.sales_history', icon: History, any_permission: ['pos.cashier.use'] },
  { to: '/app/doctor', label_key: 'nav.doctor_pos', icon: Stethoscope, any_permission: ['pos.doctor.use'] },
  { to: '/app/display/waiting-room', label_key: 'nav.waiting_room', icon: Monitor, any_permission: ['display.waiting_room.view'] },
  { to: '/app/display/medical-queue', label_key: 'nav.medical_queue', icon: Monitor, any_permission: ['display.medical_queue.view'] },
  { to: '/app/customers', label_key: 'nav.customers', icon: Users, any_permission: ['customers.read'] },
  { to: '/app/catalog', label_key: 'nav.catalog', icon: PackageSearch, any_permission: ['catalog.products.read', 'catalog.services.read'] },
  { to: '/app/inventory', label_key: 'nav.inventory', icon: ClipboardList, any_permission: ['inventory.read'] },
  { to: '/app/cash', label_key: 'nav.cash', icon: Wallet, any_permission: ['cash.shift.open', 'cash.movement.cash_in', 'pos.cashier.use'] },
  { to: '/app/open-accounts', label_key: 'nav.open_accounts', icon: BookOpen, any_permission: ['open_accounts.read'] },
  { to: '/app/invoicing', label_key: 'nav.invoicing', icon: Receipt, any_permission: ['invoicing.cfdi.request'] },
  { to: '/app/audit', label_key: 'nav.audit', icon: ShieldCheck, any_permission: ['audit.view'] },
  { to: '/app/otp', label_key: 'nav.otp_admin', icon: KeyRound, any_permission: ['admin.users.manage'] },
  { to: '/app/users', label_key: 'nav.users_admin', icon: Users, any_permission: ['admin.users.manage'] },
  { to: '/app/pricing', label_key: 'nav.pricing', icon: Tag, any_permission: ['pricing.rules.read'] },
  { to: '/app/clinical', label_key: 'nav.clinical', icon: Stethoscope, any_permission: ['clinical.visits.write'] },
  { to: '/app/hospitalization', label_key: 'nav.hospitalization', icon: HeartPulse, any_permission: ['hospitalization.records.read'] },
  { to: '/app/appointments', label_key: 'nav.appointments', icon: CalendarClock, any_permission: ['appointments.read'] },
  { to: '/app/settings', label_key: 'nav.settings', icon: SettingsIcon, any_permission: ['admin.users.manage', 'display.waiting_room.manage'] },
];

interface SidebarProps {
  user: AuthUser;
}

export function Sidebar({ user }: SidebarProps): JSX.Element {
  const { t } = useTranslation();

  const visible_items = NAV_ITEMS.filter((item) => {
    if (!item.any_permission || item.any_permission.length === 0) return true;
    return item.any_permission.some((p) => user.permissions.includes(p));
  });

  return (
    <aside className="hidden w-64 shrink-0 border-r border-border bg-card/40 px-3 py-4 md:flex md:flex-col">
      <div className="px-3 pb-4">
        <div className="rounded-xl bg-white p-2.5 shadow-sm ring-1 ring-black/5">
          <img
            src={logo_novapet_image}
            alt={t('common.app_name')}
            className="h-auto w-full object-contain"
          />
        </div>
      </div>
      <nav className="flex-1 space-y-1">
        {visible_items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-foreground/80 hover:bg-secondary hover:text-foreground',
              )
            }
          >
            <item.icon className="h-4 w-4" aria-hidden />
            <span>{t(item.label_key)}</span>
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
